import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Preloader, useResponsive } from '@common-fe/common-fe';

import { ContentWrapper, ModalWrapper } from '@/components/wrappers';
import { useSetDefaultPaymentMethodQuery } from '@/modules/transaction/components/ReimburseMe/queries/useUpdatePaymentMethod.query';
import { useStore } from '@/modules/transaction/components/ReimburseMe/store/usePaymentMethods.store';
import { useOrganizationProperties } from '@/modules/transaction/hooks';
import { AllowedPaymentMethodConfig, PaymentMethodType } from '@/modules/user/user.types';

import { usePaymentMethodsCards } from './hooks/usePaymentMethodsCards';
import AddCard from './AddCard';
import ChoosePaymentMethodFooter from './ChoosePaymentMethodFooter';
import { PaymentMethodLayout } from './paymentMethodLayout.types';

interface Props {
  onClose: () => void;
  isLoading?: boolean;
  chooseDeposit?: () => void;
  chooseDebitCard?: () => void;
  choosePayPal?: () => void;
  chooseVenmo?: () => void;
  chooseCheck?: () => void;
  removePaymentMethod?: () => void;
  editPaymentMethod?: () => void;
  onlyAddingMode?: boolean;
  onSave?: (id?: string) => void;
  allowedPaymentMethodConfigs?: AllowedPaymentMethodConfig[];
  isUnwrapped?: boolean;
  isRejectionFlow?: boolean;
  isRefund?: boolean;
  paymentMethodLayout?: PaymentMethodLayout;
  cardFooterRenderWrapper?: (paymentMethodType?: PaymentMethodType) => React.ReactNode;
}

const ChoosePaymentMethod: React.FC<Props> = ({
  onClose,
  isLoading,
  chooseDeposit,
  chooseDebitCard,
  choosePayPal,
  chooseVenmo,
  chooseCheck,
  removePaymentMethod,
  editPaymentMethod,
  onlyAddingMode,
  onSave,
  allowedPaymentMethodConfigs,
  isUnwrapped,
  isRejectionFlow,
  isRefund,
  paymentMethodLayout,
  cardFooterRenderWrapper,
}) => {
  const { isMobile } = useResponsive();
  const { t } = useTranslation();
  const paymentMethods = useStore((state) => state.methods);
  const { organizationProperties } = useOrganizationProperties();
  const { updateDefaultPaymentMethod } = useSetDefaultPaymentMethodQuery();
  const allowedPaymentMethods = useMemo((): PaymentMethodType[] => {
    if (allowedPaymentMethodConfigs?.length) {
      return allowedPaymentMethodConfigs.map((item) => item.paymentMethodType as PaymentMethodType);
    }
    return organizationProperties?.allowedPaymentMethods || [];
  }, [allowedPaymentMethodConfigs, organizationProperties]);
  const cards = usePaymentMethodsCards({
    onClose,
    chooseDeposit,
    chooseDebitCard,
    choosePayPal,
    chooseVenmo,
    chooseCheck,
    methods: paymentMethods,
    allowedPaymentMethods,
  });

  const isAlreadyHasCheckPaymentMethod = useMemo(
    () => !!paymentMethods?.find((paymentMethod) => paymentMethod?.type === PaymentMethodType.CHECK),
    [paymentMethods]
  );

  const availableCardsToAdd = useMemo(() => {
    if (onlyAddingMode && isAlreadyHasCheckPaymentMethod) {
      return cards.filter((card) => card.paymentType !== PaymentMethodType.CHECK);
    }

    return cards;
  }, [cards, onlyAddingMode, isAlreadyHasCheckPaymentMethod]);

  const renderedCards = useMemo(
    () => (
      <Box direction="column" gap="spacing8">
        {availableCardsToAdd.map((card) => (
          <AddCard
            key={card.paymentType}
            title={card.title}
            paymentType={card.paymentType}
            description={card.description}
            add={card.add}
            addTitle={card.addTitle}
            paymentMethods={card.paymentMethods}
            removePaymentMethod={removePaymentMethod}
            editPaymentMethod={editPaymentMethod}
            updateDefaultPaymentMethod={updateDefaultPaymentMethod}
            onlyAddingMode={onlyAddingMode}
            hideIcon
            allowedPaymentMethodConfigs={
              allowedPaymentMethodConfigs || organizationProperties?.allowedPaymentMethodConfigs
            }
            isUnwrapped={isUnwrapped}
            isRejectionFlow={isRejectionFlow}
            paymentMethodLayout={paymentMethodLayout}
            cardFooterRenderWrapper={cardFooterRenderWrapper}
          />
        ))}
      </Box>
    ),
    [
      availableCardsToAdd,
      removePaymentMethod,
      editPaymentMethod,
      onlyAddingMode,
      allowedPaymentMethodConfigs,
      organizationProperties,
      isUnwrapped,
      isRejectionFlow,
      paymentMethodLayout,
      updateDefaultPaymentMethod,
      cardFooterRenderWrapper,
    ]
  );

  if (isUnwrapped) {
    return (
      <Box>
        {isLoading ? (
          <Box pad="spacing12">
            <Preloader />
          </Box>
        ) : (
          renderedCards
        )}
      </Box>
    );
  }

  return (
    <ModalWrapper
      visible
      contentMobileStyle={isMobile ? { padding: 0 } : undefined}
      title={t(isRefund ? 'Choose a Refund Method' : 'Choose a Payment Method')}
      onSetVisible={onClose}
    >
      {isLoading ? <Preloader /> : <ContentWrapper isMobileView={isMobile}>{renderedCards}</ContentWrapper>}

      {!onlyAddingMode && !isLoading ? (
        <ChoosePaymentMethodFooter
          onClose={onClose}
          onSave={onSave}
          updateDefaultPaymentMethod={updateDefaultPaymentMethod}
          isRefund={isRefund}
        />
      ) : null}
    </ModalWrapper>
  );
};

export default ChoosePaymentMethod;
